import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IGetTotpRes, ISetupTotpRes } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { useTwoFactorSettings } from "store/two-factor/two-factor-settings.store";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  status: IGetTotpRes | null;
  setup: ISetupTotpRes | null;
  recoveryCodes: string[] | null;
};

type Action = {
  fetchStatus: () => Promise<void>;
  beginSetup: (password?: string) => Promise<void>;
  verifySetup: (code: string) => Promise<void>;
  revoke: (password?: string) => Promise<void>;
  clearSetup: () => void;
  clearRecoveryCodes: () => void;
};

export const useTotp = create(
  devtools<State & Action>(
    (set, get) => ({
      status: null,
      setup: null,
      recoveryCodes: null,

      fetchStatus: async () => {
        try {
          const status = await apiV2.totp.get();
          set({ status });
        } catch (error) {
          console.error("Failed to fetch authenticator status:", error);
          set({ status: { configured: false, verifiedAt: null } });
        }
      },

      beginSetup: async (password) => {
        const setup = await apiV2.totp.setup({ password });
        set({ setup });
      },

      verifySetup: async (code) => {
        const { recoveryCodes } = await apiV2.totp.verifySetup({ code });
        set({
          setup: null,
          recoveryCodes: recoveryCodes?.length ? recoveryCodes : null,
        });
        useTwoFactorSettings.getState().setMethodConfigured("totp", true);
        await get().fetchStatus();
      },

      revoke: async (password) => {
        await apiV2.totp.revoke({ password });
        useTwoFactorSettings.getState().setMethodConfigured("totp", false);
        await get().fetchStatus();
      },

      clearSetup: () => set({ setup: null }),
      clearRecoveryCodes: () => set({ recoveryCodes: null }),
    }),
    { name: "totp" }
  )
);

registerStore(useTotp);
